import { useCallback, useContext } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { UserContext } from '../../components/UserContext';
import { post_api_headers } from '../../utils/Store';
import { AUTH_SCOPES } from '../../config/Auth.config';

const BASE_URL = process.env.REACT_APP_API_USER_PROFILE;

/**
 * @param {Function} onEulaRequired  – called with userId when EULA hasn't been accepted
 */
export const useUserProfile = (onEulaRequired) => {
  const { instance } = useMsal();
  const { setUserProfile, setLoading } = useContext(UserContext);
  const navigate = useNavigate();
  const post_headers = useAtom(post_api_headers)[0];

  const fetchUserProfile = useCallback(
    async (token) => {
      const idToken = token ?? sessionStorage.getItem('id_token');
      if (!idToken) {
        setLoading(false);
        return null;
      }

      try {
        const response = await fetch(`${BASE_URL}/api/users/user-profile`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${idToken}`, ...post_headers },
          body: JSON.stringify({ token: idToken }),
          cache: 'no-store',
        });

        if (response.status === 401) {
          const account = instance.getActiveAccount();
          if (account) {
            const refreshed = await instance.acquireTokenSilent({
              scopes: AUTH_SCOPES,
              account,
              forceRefresh: true,
            });
            if (refreshed.idToken) {
              sessionStorage.setItem('id_token', refreshed.idToken);
              return fetchUserProfile(refreshed.idToken);
            }
          }
          navigate('/unauthuser');
          return null;
        }

        if (!response.ok) throw new Error(`API error: ${response.status}`);

        const data = await response.json();
        sessionStorage.setItem('userProfile', JSON.stringify(data));
        setUserProfile(data);
        setLoading(false);

        if (!data.eulaAccepted && onEulaRequired) {
          onEulaRequired(data.userId);
        }

        return data;
      } catch {
        setLoading(false);
        return null;
      }
    },
    [instance, navigate, post_headers, setLoading, setUserProfile, onEulaRequired],
  );

  return { fetchUserProfile };
};